import { type JSX, Show, createSignal } from 'solid-js'
import './Avatar.css'
import { AVATAR_PALETTE_SIZE, avatarColorIndex, avatarInitials } from './avatarHash'

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
export type AvatarShape = 'circle' | 'squircle'
export type AvatarTone = 'solid' | 'tint' | 'bare' | 'logo'

export interface AvatarProps {
  src?: string | null
  name: string
  size?: AvatarSize
  shape?: AvatarShape
  colorIndex?: number
  alt?: string
  style?: JSX.CSSProperties
  title?: string | undefined
  class?: string
  icon?: JSX.Element
  tone?: AvatarTone
  art?: JSX.Element
}

const inPalette = (index: number) =>
  Number.isInteger(index) && index >= 0 && index < AVATAR_PALETTE_SIZE ? index : 0

export function Avatar(props: AvatarProps) {
  const [failedSrc, setFailedSrc] = createSignal<string | null>(null)
  const src = () => (props.src && props.src !== failedSrc() ? props.src : null)
  const label = () => props.alt ?? props.name
  const colorIndex = () => inPalette(props.colorIndex ?? avatarColorIndex(props.name))
  const classes = () =>
    [
      'ds-avatar',
      `ds-avatar--${props.size ?? 'md'}`,
      `ds-avatar--${props.shape ?? 'circle'}`,
      `ds-avatar--c${colorIndex()}`,
      props.tone === 'tint' ? 'ds-avatar--tint' : '',
      props.tone === 'bare' ? 'ds-avatar--bare' : '',
      props.tone === 'logo' ? 'ds-avatar--logo' : '',
      props.class,
    ]
      .filter(Boolean)
      .join(' ')

  return (
    <Show
      when={src()}
      fallback={
        <span
          class={classes()}
          style={props.style}
          title={props.title}
          role={label() ? 'img' : undefined}
          aria-label={label() || undefined}
          aria-hidden={label() ? undefined : 'true'}
        >
          <Show
            when={props.art}
            fallback={
              <Show
                when={props.icon}
                fallback={
                  <span class="ds-avatar__initials" aria-hidden="true">
                    {avatarInitials(props.name)}
                  </span>
                }
              >
                <span class="ds-avatar__icon" aria-hidden="true">
                  {props.icon}
                </span>
              </Show>
            }
          >
            <span class="ds-avatar__art" aria-hidden="true">
              {props.art}
            </span>
          </Show>
        </span>
      }
    >
      {(resolved) => (
        <span class={classes()} style={props.style} title={props.title}>
          <img
            class="ds-avatar__img"
            src={resolved()}
            alt={label()}
            onError={() => setFailedSrc(resolved())}
          />
        </span>
      )}
    </Show>
  )
}
